import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import apiClient from "@/lib/apiClient";
import { FileText, Shield, Scale, ArrowLeft } from "lucide-react";

interface LegalDocumentData {
  content: string | null;
  redirect_url?: string;
  label: string;
  version?: number;
  published_at?: string;
}

const iconMap: Record<string, React.ReactNode> = {
  terms: <FileText className="w-6 h-6" />,
  privacy: <Shield className="w-6 h-6" />,
  trade_law: <Scale className="w-6 h-6" />,
};

// Map URL path to API document type
const pathToTypeMap: Record<string, string> = {
  "/terms": "terms",
  "/privacy": "privacy",
  "/trade-law": "trade_law",
};

interface LegalDocumentPageProps {
  siteId: number;
}

const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({ siteId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const documentType = pathToTypeMap[location.pathname];
  const [document, setDocument] = useState<LegalDocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDocument = async () => {
      if (!documentType) return;

      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get(`/customer/sites/${siteId}/document/${documentType}`);
        const data = response.data;

        // If there's a redirect URL, redirect to it
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
          return;
        }

        setDocument(data);
      } catch (err: any) {
        setError(err.response?.data?.error || "文書の取得に失敗しました");
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [documentType]);

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <div className="text-theme-bg-text opacity-60">読み込み中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-theme"
          >
            <ArrowLeft className="w-4 h-4" />
            戻る
          </button>
        </div>
      </div>
    );
  }

  if (!document?.content) {
    return (
      <div className="min-h-screen bg-theme-bg">
        <Helmet>
          <title>{document?.label || "法的文書"}</title>
        </Helmet>
        <div className="container mx-auto max-w-screen-md px-4 pt-20 pb-12">
          <div className="bg-white border-theme rounded-theme-lg p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 border-theme rounded-full flex items-center justify-center mx-auto mb-4">
              {iconMap[documentType || "terms"]}
            </div>
            <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-theme-bg-text mb-4">{document?.label}</h1>
            <p className="text-gray-600">この文書はまだ登録されていません。</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-black text-white [font-weight:var(--theme-font-weight-button)] rounded-theme hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              戻る
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-bg">
      <Helmet>
        <title>{document.label}</title>
      </Helmet>
      <div className="container mx-auto max-w-screen-md px-4 pt-20 pb-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70 hover:opacity-100 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            戻る
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-theme-primary border-theme rounded-theme-lg flex items-center justify-center">
              {iconMap[documentType || "terms"]}
            </div>
            <div>
              <h1 className="text-2xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">{document.label}</h1>
              {document.published_at && (
                <p className="text-sm text-theme-bg-text opacity-60">
                  最終更新: {new Date(document.published_at).toLocaleDateString("ja-JP")}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white border-theme rounded-theme-lg p-6 md:p-8">
          <div
            className="prose prose-sm max-w-none prose-headings:[font-weight:var(--theme-font-weight)] prose-headings:text-black prose-p:text-gray-700 prose-li:text-gray-700"
            dangerouslySetInnerHTML={{ __html: document.content }}
          />
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentPage;
